import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import CallCenter from './CallCenter';
import TrackedNotice from './TrackedNotice';
import './CallCenter.css';

const ParentDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const storedParent = useMemo(() => JSON.parse(localStorage.getItem('parentUser') || 'null'), []);
  const parent = location.state?.parent || storedParent;
  const [performance, setPerformance] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [securityCodes, setSecurityCodes] = useState({});
  const [fees, setFees] = useState({});
  const [progressUpdates, setProgressUpdates] = useState([]);
  const [notificationError, setNotificationError] = useState('');
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');

  const children = useMemo(() => parent?.children || [], [parent]);

  useEffect(() => {
    if (!parent) { navigate('/parent-login', { replace: true }); return; }
    let stopped = false;
    fetch(`${API_ENDPOINTS.BASE_URL}/api/session/me`, { credentials: 'include', cache: 'no-store' })
      .then(async response => {
        if (!response.ok) throw new Error('Please sign in again to open the parent portal.');
        const data = await response.json();
        if (stopped) return;
        if (data.role !== 'parent' || data.user.id !== parent.id) throw new Error('Please sign in with your parent account.');
        if (data.requiresPasswordChange) {
          localStorage.setItem('tempParentUser', JSON.stringify(data.user));
          localStorage.removeItem('parentUser');
          navigate('/parent/change-password', { replace: true, state: { parent: data.user } });
          return;
        }
        setSessionReady(true);
      }).catch(error => { if (!stopped) setSessionError(error.message); });
    return () => { stopped = true; };
  }, [parent, navigate]);

  useEffect(() => {
    if (!parent || !sessionReady) return;
    fetch(`${API_ENDPOINTS.BASE_URL}/api/announcements/parents/${parent.id}`, { credentials: 'include' })
      .then((response) => { if (!response.ok) throw new Error('Please sign in again to load your school notifications.'); return response.json(); })
      .then(setAnnouncements)
      .catch(error => { setAnnouncements([]); setNotificationError(error.message); });

    fetch(`${API_ENDPOINTS.BASE_URL}/api/parents/${parent.id}/security-codes`)
      .then((response) => response.ok ? response.json() : [])
      .then((codes) => {
        const codeMap = {};
        codes.forEach((code) => {
          codeMap[code.username] = code;
        });
        setSecurityCodes(codeMap);
      })
      .catch(() => setSecurityCodes({}));

    fetch(`${API_ENDPOINTS.BASE_URL}/api/progress-media/parent/${parent.id}`, { credentials: 'include' })
      .then((response) => response.ok ? response.json() : [])
      .then(setProgressUpdates)
      .catch(() => setProgressUpdates([]));

    children.forEach(async (child) => {
      try {
        const [progressRes, attendanceRes, feeRes] = await Promise.all([
          fetch(`${API_ENDPOINTS.BASE_URL}/api/students/progress/${child.username}`),
          fetch(`${API_ENDPOINTS.BASE_URL}/api/students/attendance/${child.username}`),
          fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/student/${child.username}`)
        ]);
        const progress = progressRes.ok ? await progressRes.json() : [];
        const attendance = attendanceRes.ok ? await attendanceRes.json() : [];
        const feeData = feeRes.ok ? await feeRes.json() : { fees: [], summary: {} };
        const average = progress.length
          ? Math.round(progress.reduce((sum, item) => sum + Number(item.score || 0), 0) / progress.length)
          : 0;
        const attendanceRate = attendance.length
          ? Math.round((attendance.filter((item) => item.status === 'present').length / attendance.length) * 100)
          : 0;
        setPerformance((current) => ({
          ...current,
          [child.username]: { average, attendanceRate, records: progress.length }
        }));
        setFees(current => ({ ...current, [child.username]: feeData }));
      } catch (error) {
        setPerformance((current) => ({ ...current, [child.username]: { average: 0, attendanceRate: 0, records: 0 } }));
      }
    });
  }, [parent, children, sessionReady]);

  const dismissAnnouncement = async announcementId => {
    try {
    const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/announcements/${announcementId}/dismiss`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: `parent:${parent.id}` })
    });
    if (response.ok) setAnnouncements(current => current.filter(item => item.id !== announcementId));
    else throw new Error((await response.json()).error || 'Unable to remove announcement');
    } catch (error) { window.alert(error.message); }
  };

  const updatesByChild = useMemo(() => progressUpdates.reduce((groups, update) => {
    const key = update.studentUsername || 'general';
    groups[key] = groups[key] || [];
    groups[key].push(update);
    return groups;
  }, {}), [progressUpdates]);

  if (!parent) return null;
  if (!sessionReady) return <div className="min-h-screen p-6 bg-slate-100 text-slate-900">
    {sessionError ? <p role="alert">{sessionError} <button className="underline" onClick={() => navigate('/parent-login')}>Sign in</button></p> : <p role="status">Checking parent account...</p>}
  </div>;

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <p className="text-sky-300 font-semibold">Parent Portal</p>
            <h1 className="text-4xl font-black mt-1">Welcome, {parent.name}</h1>
            <p className="text-slate-300 mt-2">{parent.email}</p>
          </div>
          <button
            onClick={async () => { await fetch(`${API_ENDPOINTS.BASE_URL}/api/session/logout`, {method:'POST',credentials:'include'}).catch(()=>{}); localStorage.removeItem('parentUser'); navigate('/parent-login'); }}
            className="bg-white text-slate-900 px-5 py-3 rounded-lg font-semibold hover:bg-sky-100"
          >
            Logout
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <button onClick={() => navigate('/parent/cameras')} className="bg-emerald-700 text-white px-5 py-3 rounded-lg font-semibold">Classroom cameras</button>
          <CallCenter
            identity={{
              type: 'parent',
              id: parent.id,
              name: parent.name || parent.email || parent.id
            }}
          />
          <div className="bg-sky-500 rounded-2xl p-5">
            <p className="text-sky-950 font-bold">Children Linked</p>
            <p className="text-4xl font-black mt-2">{children.length}</p>
          </div>
          <div className="bg-fuchsia-500 rounded-2xl p-5">
            <p className="text-fuchsia-950 font-bold">Video Calls</p>
            <p className="text-2xl font-black mt-2">{parent.videoCallStart && parent.videoCallEnd ? `${parent.videoCallStart} - ${parent.videoCallEnd}` : 'By schedule'}</p>
          </div>
        </div>

        <div className="bg-white text-slate-900 rounded-2xl p-5 shadow-xl mb-8">
          {notificationError && <p role="alert" className="text-red-700 mb-4">{notificationError} <button className="underline" onClick={() => navigate('/parent-login')}>Sign in</button></p>}
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-2xl font-black">School Announcements</h2>
            <span className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm font-bold">{announcements.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {announcements.map((announcement) => (
              <TrackedNotice key={announcement.id} item={announcement} className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <p className="font-black">{announcement.title}</p>
                  <button onClick={() => dismissAnnouncement(announcement.id)} className="text-xs bg-slate-800 text-white px-2 py-1 rounded" title="Mark read and remove">Remove</button>
                </div>
                <p className="text-sm text-slate-600 mt-3 whitespace-pre-line">{announcement.content}</p>
                </div>
              </TrackedNotice>
            ))}
            {!announcements.length && <p className="text-slate-600">No parent announcements right now.</p>}
          </div>
        </div>

        <section className="bg-white text-slate-900 rounded-2xl p-5 shadow-xl mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <p className="text-emerald-700 font-black">School Fees</p>
              <h2 className="text-2xl font-black">Family Fee Position</h2>
            </div>
            <span className={`px-3 py-2 rounded-lg font-black ${
              children.some(child => Number(fees[child.username]?.summary?.totalBalance || 0) > 0)
                ? 'bg-rose-100 text-rose-800' : 'bg-emerald-100 text-emerald-800'
            }`}>
              {children.some(child => Number(fees[child.username]?.summary?.totalBalance || 0) > 0) ? 'Payment outstanding' : 'All fees paid'}
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {children.map(child => {
              const data = fees[child.username] || { fees: [], summary: {} };
              return (
                <div key={child.username} className="border border-slate-200 rounded-xl p-4">
                  <div className="flex justify-between gap-3">
                    <div>
                      <h3 className="font-black">{child.firstName} {child.lastName}</h3>
                      <p className="text-sm text-slate-500">{child.className}</p>
                    </div>
                    <strong className={Number(data.summary.totalBalance || 0) > 0 ? 'text-rose-700' : 'text-emerald-700'}>
                      {Number(data.summary.totalBalance || 0) > 0 ? 'Owing' : 'Paid'}
                    </strong>
                  </div>
                  <div className="grid grid-cols-3 gap-2 my-4 text-sm">
                    <div className="bg-slate-100 p-3 rounded"><span className="block text-slate-500">Required</span><b>NGN {Number(data.summary.totalExpected || 0).toLocaleString()}</b></div>
                    <div className="bg-emerald-50 p-3 rounded"><span className="block text-emerald-700">Paid</span><b>NGN {Number(data.summary.totalPaid || 0).toLocaleString()}</b></div>
                    <div className="bg-rose-50 p-3 rounded"><span className="block text-rose-700">Balance</span><b>NGN {Number(data.summary.totalBalance || 0).toLocaleString()}</b></div>
                  </div>
                  <div className="space-y-2">
                    {data.fees.slice(0, 4).map(fee => (
                      <div key={fee.feeId} className="flex justify-between text-sm border-t pt-2">
                        <span>{fee.feeName}</span>
                        <b className={fee.status === 'paid' ? 'text-emerald-700' : 'text-rose-700'}>{fee.status} · NGN {Number(fee.balance || 0).toLocaleString()}</b>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => navigate('/student/fees', { state: { student: child, parent } })} className="mt-4 w-full bg-emerald-700 text-white py-3 rounded-lg font-black">
                    {Number(data.summary.totalBalance || 0) > 0 ? 'Pay with Flutterwave' : 'View fee details'}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-white text-slate-900 rounded-2xl p-5 shadow-xl mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <p className="text-cyan-700 font-black">Child Progress Gallery</p>
              <h2 className="text-2xl font-black">Teacher Updates</h2>
            </div>
            <span className="bg-cyan-100 text-cyan-800 px-3 py-2 rounded-lg font-black">{progressUpdates.length} updates</span>
          </div>

          {progressUpdates.length === 0 ? (
            <p className="text-slate-600">No teacher photo or video updates have been posted yet.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {progressUpdates.map((update) => (
                <TrackedNotice key={update.id} type="progress" item={update} className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                  <div className="bg-slate-900 aspect-video flex items-center justify-center">
                    {update.fileType === 'video' ? (
                      <video src={`${API_ENDPOINTS.BASE_URL}${update.mediaPath}`} controls className="w-full h-full object-contain" />
                    ) : (
                      <img src={`${API_ENDPOINTS.BASE_URL}${update.mediaPath}`} alt={update.title} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-black">{update.title}</h3>
                        <p className="text-sm text-slate-500">{update.studentName} · {update.className}</p>
                      </div>
                      <span className="text-xs bg-cyan-100 text-cyan-800 px-2 py-1 rounded-full font-bold">{update.fileType}</span>
                    </div>
                    {update.description && <p className="text-sm text-slate-700 mt-3 whitespace-pre-line">{update.description}</p>}
                    <p className="text-xs text-slate-500 mt-3">
                      {update.teacherName} · {new Date(update.createdAt).toLocaleString()}
                    </p>
                  </div>
                </TrackedNotice>
              ))}
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {children.map((child) => (
            <div key={child.username} className="bg-white text-slate-900 rounded-2xl p-5 shadow-xl">
              <div className="flex items-center gap-4">
                <img
                  src={child.profilePicture || '/fallback-data/default-avatar.png'}
                  alt={`${child.firstName} ${child.lastName}`}
                  className="w-16 h-16 rounded-2xl object-cover"
                />
                <div>
                  <h2 className="text-2xl font-black">{child.firstName} {child.lastName}</h2>
                  <p className="text-slate-600">{child.className} - {child.username}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                <div className="sm:col-span-2 grid grid-cols-3 gap-2">
                  <div className="bg-slate-100 rounded-lg p-3">
                    <p className="text-xs text-slate-500 font-bold">Average</p>
                    <p className="text-xl font-black">{performance[child.username]?.average || 0}%</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3">
                    <p className="text-xs text-slate-500 font-bold">Attendance</p>
                    <p className="text-xl font-black">{performance[child.username]?.attendanceRate || 0}%</p>
                  </div>
                  <div className="bg-slate-100 rounded-lg p-3">
                    <p className="text-xs text-slate-500 font-bold">Records</p>
                    <p className="text-xl font-black">{performance[child.username]?.records || 0}</p>
                  </div>
                </div>
                <button onClick={() => navigate('/student/fees', { state: { student: child, parent } })} className="sm:col-span-2 bg-emerald-600 text-white py-3 rounded-lg font-semibold hover:bg-emerald-700">View Fee Account</button>
              </div>
              <p className="mt-3 text-sm text-slate-500">
                Call window: {parent.videoCallStart && parent.videoCallEnd ? `${parent.videoCallStart} - ${parent.videoCallEnd}` : 'Ask the school admin to set a call time.'}
              </p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
                  <p className="text-xs uppercase font-black text-amber-700">Drop-off PIN</p>
                  <p className="text-3xl font-black tracking-widest text-amber-950 mt-1">
                    {securityCodes[child.username]?.dropoff_code || 'Not set'}
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    {securityCodes[child.username]?.dropoff_verified ? 'Already verified' : 'Use at school gate'}
                  </p>
                </div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
                  <p className="text-xs uppercase font-black text-emerald-700">Pickup PIN</p>
                  <p className="text-3xl font-black tracking-widest text-emerald-950 mt-1">
                    {securityCodes[child.username]?.pickup_code || 'Not set'}
                  </p>
                  <p className="text-xs text-emerald-700 mt-1">
                    {securityCodes[child.username]?.pickup_verified ? 'Already verified' : 'Use at school gate'}
                  </p>
                </div>
              </div>
              {updatesByChild[child.username]?.length > 0 && (
                <div className="mt-4 rounded-xl bg-cyan-50 border border-cyan-200 p-4">
                  <p className="text-xs uppercase font-black text-cyan-700">Latest Teacher Update</p>
                  <p className="font-black text-cyan-950 mt-1">{updatesByChild[child.username][0].title}</p>
                  <p className="text-sm text-cyan-800 mt-1">{updatesByChild[child.username][0].description || 'Media update available in the gallery section.'}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        {children.length === 0 && (
          <div className="bg-white text-slate-900 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold">No children linked yet</h2>
            <p className="text-slate-600 mt-2">Ask the school administrator to link your parent account to your child.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentDashboard;
